// The amenity registry: every feature a listing can declare, with one stable
// key, one canonical label, a group, and the flags the rest of the product
// hangs off (renter facets, owner essentials, card tags, the legacy boolean
// column it feeds, schema.org names, synonyms in English/Spanish/legacy).
//
// Storage stays LABELS in `properties.amenities[]` (and explicit "no" answers
// in `properties.amenities_absent[]`); keys are the code-level identity used
// in feeds, analytics, the concierge tool and collections. Renaming a label
// is a registry alias + one `array_replace` migration (see normalizeAmenities).
//
// Design: a long grouped checklist for owners (~50 items in 11 groups), a
// short curated facet set for renters (12), and 10 explicit essentials,
// the questions renters in a beach town ask before they write.
import { foldKey } from "@/lib/foldKey";

export type AmenityGroup =
  | "climate"
  | "kitchen"
  | "laundry"
  | "connectivity"
  | "outdoor"
  | "views"
  | "wellness"
  | "safety"
  | "family"
  | "entertainment"
  | "access"
  | "pets";

/** Legacy boolean columns on `properties` that a label implies. */
export type AmenityBooleanColumn = "pool" | "ocean_view" | "pet_friendly" | "ac" | "internet" | "parking" | "security";

export interface AmenitySpec {
  /** Stable slug: URLs (accepted), feed, analytics, concierge enum, collections. */
  key: string;
  /** Canonical stored value in properties.amenities[]. */
  label: string;
  group: AmenityGroup;
  /** Owners answer these explicitly (yes / no); renters ask about them first. */
  essential?: boolean;
  /** Promoted renter facet (browse chips, quiz, profile, alerts). */
  filterable?: boolean;
  /** lucide icon name; AmenityIcon falls back to a check mark. */
  icon: string;
  /** One-line owner hint shown next to an essential. */
  hint?: string;
  /** Alternative spellings, Spanish terms, legacy labels, resolved case/accent-insensitively. */
  synonyms?: string[];
  /** schema.org / Google vacation-rental token for JSON-LD `propertyID`, when one exists. */
  schemaName?: string;
  booleanColumn?: AmenityBooleanColumn;
  /** > 0 ⇒ eligible as a card tag; cards show the top three present. */
  cardWeight?: number;
  /** The pet-type labels: stored in amenities[], never shown in grids. */
  petType?: boolean;
}

export const AMENITY_GROUPS: ReadonlyArray<{ id: AmenityGroup; label: string }> = [
  { id: "climate", label: "Climate & comfort" },
  { id: "kitchen", label: "Kitchen & dining" },
  { id: "laundry", label: "Laundry" },
  { id: "connectivity", label: "Work & internet" },
  { id: "outdoor", label: "Outdoor" },
  { id: "views", label: "Views" },
  { id: "wellness", label: "Wellness & fitness" },
  { id: "safety", label: "Safety & security" },
  { id: "family", label: "Family" },
  { id: "entertainment", label: "Entertainment" },
  { id: "access", label: "Access & building" },
  { id: "pets", label: "Pets" },
];

const E = true;

export const AMENITIES: readonly AmenitySpec[] = [
  // --- climate & comfort
  { key: "ac", label: "A/C", group: "climate", essential: E, filterable: E, icon: "AirVent", hint: "Air conditioning in at least one room", synonyms: ["Air Conditioning", "AC", "aire acondicionado", "aire"], schemaName: "ac", booleanColumn: "ac", cardWeight: 70 },
  { key: "ac_bedrooms", label: "A/C in Every Bedroom", group: "climate", icon: "AirVent", synonyms: ["aire en todas las habitaciones"] },
  { key: "ceiling_fans", label: "Ceiling Fans", group: "climate", icon: "Fan", synonyms: ["fans", "abanicos", "ventiladores"] },
  { key: "hot_water", label: "Hot Water", group: "climate", icon: "Droplets", synonyms: ["agua caliente"] },
  { key: "heating", label: "Heating", group: "climate", icon: "Thermometer", synonyms: ["calefacción", "calefaccion"], schemaName: "heating" },
  { key: "screens", label: "Mosquito Screens", group: "climate", essential: E, icon: "BugOff", hint: "Screens on windows and doors, or nets over the beds", synonyms: ["insect screens", "mosquito nets", "mosquiteros", "cedazo"] },

  // --- kitchen & dining
  { key: "kitchen", label: "Equipped Kitchen", group: "kitchen", essential: E, icon: "CookingPot", hint: "Pots, pans, dishes and a stove ready to cook", synonyms: ["full kitchen", "kitchen", "cocina equipada", "cocina"], schemaName: "kitchen" },
  { key: "dishwasher", label: "Dishwasher", group: "kitchen", essential: E, filterable: E, icon: "Utensils", hint: "A dishwasher in the kitchen", synonyms: ["dish washer", "lavavajillas", "lavaplatos"], cardWeight: 60 },
  { key: "oven", label: "Oven", group: "kitchen", icon: "Microwave", synonyms: ["stove", "horno"], schemaName: "ovenStove" },
  { key: "microwave", label: "Microwave", group: "kitchen", icon: "Microwave", synonyms: ["microondas"], schemaName: "microwave" },
  { key: "coffee_maker", label: "Coffee Maker", group: "kitchen", icon: "Coffee", synonyms: ["coffee machine", "espresso machine", "nespresso", "cafetera"] },
  { key: "drinking_water", label: "Drinking Water Filter", group: "kitchen", icon: "GlassWater", synonyms: ["water filter", "filtered water", "agua potable", "filtro de agua"] },
  { key: "outdoor_dining", label: "Outdoor Dining", group: "kitchen", icon: "UtensilsCrossed", synonyms: ["comedor exterior"] },
  { key: "bbq", label: "BBQ / Grill", group: "kitchen", icon: "Flame", synonyms: ["bbq", "barbecue", "grill", "parrilla", "asador"], schemaName: "outdoorGrill" },

  // --- laundry
  { key: "washer", label: "Washing machine", group: "laundry", essential: E, filterable: E, icon: "WashingMachine", hint: "A washing machine in the home, not a laundry service", synonyms: ["Washer", "Laundry", "washing machine", "lavadora", "lavandería", "lavanderia"], schemaName: "washerDryer", cardWeight: 55 },
  { key: "dryer", label: "Dryer", group: "laundry", icon: "WashingMachine", synonyms: ["tumble dryer", "clothes dryer", "secadora"] },
  { key: "linens", label: "Linens & Towels", group: "laundry", icon: "Shirt", synonyms: ["linens", "towels", "sábanas y toallas", "sabanas"] },

  // --- work & internet
  { key: "wifi", label: "WiFi", group: "connectivity", essential: E, filterable: E, icon: "Wifi", hint: "Wi-Fi in the home", synonyms: ["wi-fi", "internet", "wireless"], schemaName: "wifi", booleanColumn: "internet", cardWeight: 30 },
  { key: "starlink", label: "Starlink", group: "connectivity", filterable: E, icon: "Satellite", synonyms: ["WiFi (Starlink)", "satellite internet"], booleanColumn: "internet", cardWeight: 45 },
  { key: "fiber", label: "Fiber Internet", group: "connectivity", icon: "Cable", synonyms: ["fiber", "fibre", "fibra óptica", "fibra"] },
  { key: "workspace", label: "Workspace", group: "connectivity", essential: E, filterable: E, icon: "Laptop", hint: "A desk or table with a proper chair to work from", synonyms: ["desk", "home office", "dedicated workspace", "escritorio", "oficina"], cardWeight: 50 },

  // --- outdoor
  { key: "pool", label: "Pool", group: "outdoor", filterable: E, icon: "Waves", synonyms: ["swimming pool", "piscina", "alberca"], schemaName: "pool", booleanColumn: "pool", cardWeight: 100 },
  { key: "hot_tub", label: "Hot Tub / Jacuzzi", group: "outdoor", icon: "Bath", synonyms: ["hot tub", "jacuzzi", "hydromassage"], schemaName: "hotTub", cardWeight: 42 },
  { key: "balcony", label: "Balcony / Terrace", group: "outdoor", icon: "Sun", synonyms: ["balcony", "terrace", "terraza", "balcón", "balcon"], schemaName: "balcony" },
  { key: "garden", label: "Garden / Yard", group: "outdoor", icon: "Trees", synonyms: ["garden", "yard", "jardín", "jardin", "patio"] },
  { key: "outdoor_shower", label: "Outdoor Shower", group: "outdoor", icon: "ShowerHead", synonyms: ["ducha exterior"] },

  // --- views
  { key: "ocean_view", label: "Ocean View", group: "views", filterable: E, icon: "Waves", synonyms: ["sea view", "vista al mar"], booleanColumn: "ocean_view", cardWeight: 90 },
  { key: "valley_view", label: "Valley View", group: "views", icon: "Mountain", synonyms: ["vista al valle"] },
  { key: "jungle_view", label: "Jungle View", group: "views", icon: "TreePalm", synonyms: ["vista a la selva"] },
  { key: "mountain_view", label: "Mountain View", group: "views", icon: "Mountain", synonyms: ["vista a la montaña", "vista a la montana"] },
  { key: "garden_view", label: "Garden View", group: "views", icon: "Flower2", synonyms: ["vista al jardín", "vista al jardin"] },

  // --- wellness & fitness
  { key: "sauna", label: "Sauna", group: "wellness", icon: "Flame" },
  { key: "cold_plunge", label: "Cold Plunge", group: "wellness", icon: "Snowflake", synonyms: ["ice bath"] },
  { key: "gym", label: "Gym / Fitness", group: "wellness", icon: "Dumbbell", synonyms: ["gym", "fitness", "gimnasio"], schemaName: "gymFitnessEquipment" },
  { key: "yoga_space", label: "Yoga Deck", group: "wellness", icon: "Sparkles", synonyms: ["yoga space", "yoga platform", "shala"] },
  { key: "surf_rack", label: "Surfboard Storage", group: "wellness", icon: "Package", synonyms: ["surf rack", "board storage", "surfboards"] },

  // --- safety & security
  { key: "security", label: "Security", group: "safety", filterable: E, icon: "ShieldCheck", synonyms: ["seguridad", "guard", "24h security"], booleanColumn: "security", cardWeight: 20 },
  { key: "gated", label: "Gated", group: "safety", icon: "Fence", synonyms: ["gated community", "portón", "porton"], booleanColumn: "security" },
  { key: "safe", label: "Safe Box", group: "safety", icon: "Lock", synonyms: ["safe", "safety box", "caja fuerte"] },

  // --- family
  { key: "crib", label: "Crib", group: "family", icon: "Baby", synonyms: ["cot", "cuna"], schemaName: "crib" },

  // --- entertainment
  { key: "tv", label: "TV", group: "entertainment", icon: "Tv", synonyms: ["television", "smart tv", "televisión", "televisor"], schemaName: "tv" },
  { key: "sound_system", label: "Sound System", group: "entertainment", icon: "Speaker", synonyms: ["speakers", "equipo de sonido"] },

  // --- access & building
  { key: "parking", label: "Parking", group: "access", essential: E, filterable: E, icon: "Car", hint: "A private spot for a car", synonyms: ["private parking", "garage", "parqueo", "estacionamiento", "garaje"], booleanColumn: "parking", cardWeight: 35 },
  { key: "generator", label: "Generator", group: "access", essential: E, filterable: E, icon: "Zap", hint: "Backup power during outages", synonyms: ["backup power", "power backup", "generador", "planta eléctrica", "planta electrica"], cardWeight: 40 },
  { key: "water_tank", label: "Water Tank", group: "access", essential: E, icon: "Container", hint: "A reserve tank for when the water supply cuts out", synonyms: ["water reserve", "water storage", "tanque de agua", "cisterna"] },
  { key: "ev_charger", label: "EV Charger", group: "access", icon: "BatteryCharging", synonyms: ["ev charging", "cargador eléctrico", "cargador electrico"] },
  { key: "elevator", label: "Elevator", group: "access", icon: "ArrowUpDown", synonyms: ["lift", "ascensor"], schemaName: "elevator" },
  { key: "wheelchair", label: "Wheelchair Accessible", group: "access", icon: "Accessibility", synonyms: ["accessible", "step-free", "silla de ruedas"], schemaName: "wheelchairAccessible" },
  { key: "private_entrance", label: "Private Entrance", group: "access", icon: "DoorOpen", synonyms: ["entrada privada"] },

  // --- pets (the "Pets allowed" section of the wizard owns these)
  { key: "pet_friendly", label: "Pet Friendly", group: "pets", filterable: E, icon: "PawPrint", synonyms: ["pets allowed", "pets", "mascotas", "se aceptan mascotas"], schemaName: "petsAllowed", booleanColumn: "pet_friendly", cardWeight: 80 },
  { key: "small_dogs", label: "Small Dogs", group: "pets", icon: "Dog", petType: E },
  { key: "medium_dogs", label: "Medium Dogs", group: "pets", icon: "Dog", petType: E },
  { key: "large_dogs", label: "Large Dogs", group: "pets", icon: "Dog", petType: E },
  { key: "cats", label: "Cats", group: "pets", icon: "Cat", petType: E },
];

const BY_KEY = new Map(AMENITIES.map((a) => [a.key, a]));
const BY_LABEL = new Map(AMENITIES.map((a) => [a.label, a]));
// Folded lookups: label, key and every synonym → spec.
const BY_TOKEN = new Map<string, AmenitySpec>();
for (const a of AMENITIES) {
  for (const t of [a.label, a.key, ...(a.synonyms ?? [])]) {
    const k = foldKey(t);
    if (k && !BY_TOKEN.has(k)) BY_TOKEN.set(k, a);
  }
}

export const amenityByKey = (key: string): AmenitySpec | undefined => BY_KEY.get(key);
export const amenityByLabel = (label: string): AmenitySpec | undefined => BY_LABEL.get(label);

/** Every renter-facing label (no pet types), registry order. */
export const AMENITY_OPTIONS: readonly string[] = AMENITIES.filter((a) => !a.petType).map((a) => a.label);
/** The owner checklist: everything except pets (owned by the Rules step). */
export const checklistAmenities = (): AmenitySpec[] => AMENITIES.filter((a) => !a.petType && a.group !== "pets");
export const checklistLabels = (): string[] => checklistAmenities().map((a) => a.label);
export const ESSENTIALS: readonly AmenitySpec[] = AMENITIES.filter((a) => a.essential);
export const FACETS: readonly AmenitySpec[] = AMENITIES.filter((a) => a.filterable);
export const FACET_LABELS: readonly string[] = FACETS.map((a) => a.label);
export const AMENITY_KEYS: readonly string[] = AMENITIES.filter((a) => !a.petType).map((a) => a.key);
export const PET_OPTIONS: readonly string[] = AMENITIES.filter((a) => a.petType).map((a) => a.label);
export const isPetOption = (value: string): boolean => PET_OPTIONS.includes(value);

/** Exact legacy spellings still found in stored rows and old drafts. */
export const LEGACY_AMENITY_ALIASES: Readonly<Record<string, string>> = {
  "WiFi (Starlink)": "Starlink",
  Laundry: "Washing machine",
  Washer: "Washing machine",
  "Air Conditioning": "A/C",
};

/** Canonicalise stored labels: legacy aliases applied, duplicates dropped, order kept. */
export const normalizeAmenities = (amenities: readonly string[] | null | undefined): string[] => {
  const out: string[] = [];
  for (const raw of amenities ?? []) {
    const label = LEGACY_AMENITY_ALIASES[raw] ?? raw;
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
};

/** A label, key, synonym or misspelt variant → the canonical label, else undefined. */
export const resolveAmenityToken = (token: string): string | undefined => {
  if (!token) return undefined;
  const direct = BY_LABEL.get(token) ?? BY_LABEL.get(LEGACY_AMENITY_ALIASES[token] ?? "");
  if (direct) return direct.label;
  return BY_TOKEN.get(foldKey(token))?.label;
};

export const labelsToKeys = (labels: readonly string[]): string[] =>
  labels.map((l) => amenityByLabel(l)?.key ?? foldKey(l)).filter(Boolean);
export const keysToLabels = (keys: readonly string[]): string[] =>
  keys.map((k) => amenityByKey(k)?.label).filter((l): l is string => !!l);

/** What a listing row (or Property) exposes about its features. */
export interface AmenitySource {
  amenities?: readonly string[] | null;
  amenitiesAbsent?: readonly string[] | null;
  pool?: boolean | null;
  oceanView?: boolean | null;
  petFriendly?: boolean | null;
  ac?: boolean | null;
  internet?: boolean | null;
  parking?: boolean | null;
  security?: boolean | null;
  parkingSpots?: number | null;
}

const BOOLEAN_LABELS: ReadonlyArray<[keyof AmenitySource, string]> = [
  ["pool", "Pool"],
  ["oceanView", "Ocean View"],
  ["petFriendly", "Pet Friendly"],
  ["ac", "A/C"],
  ["internet", "WiFi"],
  ["parking", "Parking"],
  ["security", "Security"],
];

/**
 * Every label the listing declares: the array (canonicalised) plus the labels
 * its legacy booleans imply. The one synthesis every matcher should share.
 */
export const presentAmenityLabels = (p: AmenitySource): string[] => {
  const out = normalizeAmenities(p.amenities);
  for (const [field, label] of BOOLEAN_LABELS) {
    if (p[field] === true && !out.includes(label)) out.push(label);
  }
  if ((p.parkingSpots ?? 0) > 0 && !out.includes("Parking")) out.push("Parking");
  return out;
};

/** Does the listing declare the amenity (label, key or synonym)? */
export const hasAmenity = (p: AmenitySource, token: string): boolean => {
  const label = resolveAmenityToken(token) ?? token;
  return presentAmenityLabels(p).includes(label);
};

/** Essentials the owner explicitly answered "no" to (never inferred). */
export const absentEssentials = (p: AmenitySource): string[] => {
  const present = new Set(presentAmenityLabels(p));
  const absent = normalizeAmenities(p.amenitiesAbsent);
  return ESSENTIALS.map((e) => e.label).filter((l) => absent.includes(l) && !present.has(l));
};

export interface AmenityGroupView {
  id: AmenityGroup | "other";
  label: string;
  items: AmenitySpec[];
}

/** Labels → groups in display order; unknown (legacy free-text) labels land in "other". Pet types are skipped. */
export const groupedAmenities = (labels: readonly string[]): AmenityGroupView[] => {
  const byGroup = new Map<AmenityGroup | "other", AmenitySpec[]>();
  for (const raw of normalizeAmenities(labels)) {
    const spec = BY_LABEL.get(raw);
    if (spec?.petType) continue;
    const id = spec?.group ?? "other";
    const item = spec ?? { key: foldKey(raw), label: raw, group: "access" as AmenityGroup, icon: "Check" };
    const list = byGroup.get(id) ?? [];
    if (!list.some((x) => x.label === item.label)) list.push(item);
    byGroup.set(id, list);
  }
  const out: AmenityGroupView[] = [];
  for (const g of AMENITY_GROUPS) {
    const items = byGroup.get(g.id);
    if (items?.length) out.push({ id: g.id, label: g.label, items });
  }
  const other = byGroup.get("other");
  if (other?.length) out.push({ id: "other", label: "More", items: other });
  return out;
};

/** The listing's most sellable declared features, heaviest first. */
export const cardTags = (p: AmenitySource, n = 3): string[] =>
  presentAmenityLabels(p)
    .map((l) => BY_LABEL.get(l))
    .filter((s): s is AmenitySpec => !!s && (s.cardWeight ?? 0) > 0)
    .sort((a, b) => (b.cardWeight ?? 0) - (a.cardWeight ?? 0))
    .slice(0, n)
    .map((s) => s.label);

export interface DerivedListingBooleans {
  pool: boolean;
  ocean_view: boolean;
  ac: boolean;
  internet: boolean;
  security: boolean;
  parking: boolean;
  pet_friendly: boolean;
}

/**
 * The legacy boolean columns, derived from the labels (the same rule table the
 * `properties_derive_amenity_flags` trigger applies server-side).
 */
export const deriveListingBooleans = (input: { amenities: readonly string[]; parkingSpots?: number | null }): DerivedListingBooleans => {
  const labels = new Set(normalizeAmenities(input.amenities));
  const has = (column: AmenityBooleanColumn) => AMENITIES.some((a) => a.booleanColumn === column && labels.has(a.label));
  // "Internet included in rent" is the owner's answer, not WiFi (Oct 17).
  const internet = has("internet");
  return {
    pool: has("pool"),
    ocean_view: has("ocean_view"),
    ac: has("ac"),
    internet,
    security: has("security"),
    parking: has("parking") || (input.parkingSpots ?? 0) > 0,
    pet_friendly: has("pet_friendly"),
  };
};

export interface AmenityCompleteness {
  answered: number;
  total: number;
  unanswered: AmenitySpec[];
}

/** How many essentials the owner has answered either way. */
export const amenityCompleteness = (p: Pick<AmenitySource, "amenities" | "amenitiesAbsent">): AmenityCompleteness => {
  const present = new Set(normalizeAmenities(p.amenities));
  const absent = new Set(normalizeAmenities(p.amenitiesAbsent));
  const unanswered = ESSENTIALS.filter((e) => !present.has(e.label) && !absent.has(e.label));
  return { answered: ESSENTIALS.length - unanswered.length, total: ESSENTIALS.length, unanswered };
};
